import React, { useMemo, useState, useEffect } from 'react';
import { Search, X, User } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

/**
 * Phase 6 — Reusable Athlete Selector Header.
 *
 * Standardised pattern for athlete-specific modules. After an athlete is
 * picked, the parent page should filter / fetch only that athlete's data.
 * The component intentionally does NOT touch the network — the parent
 * owns the fetch, this just owns the picker UX.
 *
 * Props
 * ─────
 * athletes        — array of {id, full_name, squad?, position?}
 * value           — currently selected athlete_id (or '' / null)
 * onChange(id)    — invoked with new athlete_id, or '' when cleared
 * label           — title above the picker (e.g. "Strength & Conditioning")
 * subtitle        — descriptive text under the title
 * placeholder     — picker placeholder when nothing is selected
 * emptyState      — node shown beneath when no athlete is selected
 * testIdPrefix    — used to namespace `data-testid` attributes
 */
const AthleteSelectorHeader = ({
  athletes = [],
  value = '',
  onChange,
  label = 'Select Athlete',
  subtitle = 'Pick an athlete to view their records.',
  placeholder = 'Search athletes…',
  emptyState = null,
  testIdPrefix = 'athlete-selector',
  children,            // rendered below when value is set
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => athletes.find(a => a.id === value) || null,
    [athletes, value]
  );

  const matches = useMemo(() => {
    if (!query.trim()) return athletes.slice(0, 25);
    const q = query.trim().toLowerCase();
    return athletes
      .filter(a => (a.full_name || '').toLowerCase().includes(q))
      .slice(0, 25);
  }, [athletes, query]);

  // Close dropdown when an athlete is picked.
  useEffect(() => {
    if (value) setOpen(false);
  }, [value]);

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-root`}>
      <Card data-testid={`${testIdPrefix}-card`}>
        <CardContent className="py-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-600" />
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                  {label}
                </h3>
              </div>
              {!selected && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {subtitle}
                </p>
              )}
              {selected && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <Badge
                    className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1 text-sm py-1 px-3"
                    data-testid={`${testIdPrefix}-selected-badge`}
                  >
                    <User className="w-3.5 h-3.5" />
                    {selected.full_name}
                    {selected.squad && (
                      <span className="text-emerald-700/70 ml-1">· {selected.squad}</span>
                    )}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => onChange?.('')}
                    data-testid={`${testIdPrefix}-clear`}
                  >
                    <X className="w-3 h-3 mr-1" /> Change athlete
                  </Button>
                </div>
              )}
            </div>

            {!selected && (
              <div className="relative w-full sm:w-80 max-w-full">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
                <Input
                  value={query}
                  onChange={e => { setQuery(e.target.value); setOpen(true); }}
                  onFocus={() => setOpen(true)}
                  placeholder={placeholder}
                  className="pl-8"
                  data-testid={`${testIdPrefix}-search`}
                />
                {open && matches.length > 0 && (
                  <div
                    className="absolute z-30 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg"
                    data-testid={`${testIdPrefix}-dropdown`}
                  >
                    {matches.map(a => (
                      <button
                        type="button"
                        key={a.id}
                        onClick={() => { onChange?.(a.id); setQuery(''); }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex justify-between items-center"
                        data-testid={`${testIdPrefix}-option-${a.id}`}
                      >
                        <span className="truncate">{a.full_name}</span>
                        {a.squad && (
                          <span className="text-[10px] text-gray-500 ml-2 shrink-0">{a.squad}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {!selected && (
        emptyState ?? (
          <div
            className="py-16 text-center text-gray-400 dark:text-gray-500 text-sm"
            data-testid={`${testIdPrefix}-empty`}
          >
            Pick an athlete above to load their records.
          </div>
        )
      )}
      {selected && children}
    </div>
  );
};

export default AthleteSelectorHeader;
